import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faBullhorn, faEnvelope, faComment, faMobileAlt,
    faPaperPlane, faTrash, faChartBar, faClock, faCheckCircle,
    faFileAlt,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { NavLink } from 'react-router-dom';
import Table from '../components/Listing/Table';
import FabGroup from '../components/FabGroup';
import CampaignCreateModal from '../components/Modal/CampaignCreateModal';
import { useAuth } from '../context/AuthProvider';
import { API_BASE_URL } from '../api';
import './Campaigns.scss';

const getCanalMeta = (t) => ({
    email:    { label: t('Email'),    icon: faEnvelope,  cls: 'camp-badge canal--email' },
    whatsapp: { label: t('WhatsApp'), icon: faComment,   cls: 'camp-badge canal--whatsapp' },
    sms:      { label: t('SMS'),      icon: faMobileAlt, cls: 'camp-badge canal--sms' },
});

const getStatutMeta = (t) => ({
    brouillon: { label: t('campaigns.status.draft'),     cls: 'camp-badge statut--brouillon', icon: faFileAlt },
    planifie:  { label: t('campaigns.status.scheduled'), cls: 'camp-badge statut--planifie',  icon: faClock },
    en_cours:  { label: t('campaigns.status.sending'),   cls: 'camp-badge statut--en_cours',  icon: faPaperPlane },
    envoye:    { label: t('campaigns.status.sent'),      cls: 'camp-badge statut--envoye',    icon: faCheckCircle },
    erreur:    { label: t('campaigns.status.error'),     cls: 'camp-badge statut--erreur',    icon: faBullhorn },
});

function StatCard({ icon, label, value, sub, color }) {
    return (
        <div className="camp-stat-card">
            <div className="camp-stat-icon" style={{ background: color + '18', color }}>
                <FontAwesomeIcon icon={icon} />
            </div>
            <div>
                <div className="camp-stat-value">{value}</div>
                <div className="camp-stat-label">{label}</div>
                {sub && <div className="camp-stat-sub">{sub}</div>}
            </div>
        </div>
    );
}

export default function Campaigns() {
    const { t } = useTranslation();
    const { setTitle, setTitleIcon } = usePageTitle();
    const navigate = useNavigate();
    const { hasRight } = useAuth();
    const canCreate = hasRight('MARKETING_CREATE');
    const canEdit = hasRight('MARKETING_EDIT');
    const canDelete = hasRight('MARKETING_DELETE');
    const [campaigns, setCampaigns] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(null);
    const [keyword, setKeyword] = useState('');
    const [showCreate, setShowCreate] = useState(false);

    const CANAL_META = getCanalMeta(t);
    const STATUT_META = getStatutMeta(t);

    useEffect(() => {
        setTitle(`${t('campaigns.title')}<span style="display:block;font-size:11px;font-weight:400;color:#9ca3af;margin-top:5px;">${t('campaigns.subtitle')}</span>`);
        document.title = t('campaigns.title');
        setTitleIcon('ti-send');
        return () => setTitleIcon(null);
    }, [setTitle, setTitleIcon, t]);

    useBreadcrumbSetup([{ label: t('Marketing'), url: '/marketing/campaigns' }]);

    const fetchAll = () => {
        Promise.all([
            axios.get(`${API_BASE_URL}/api/v1/marketing/campaigns`, { withCredentials: true }),
            axios.get(`${API_BASE_URL}/api/v1/marketing/campaigns/stats`, { withCredentials: true }),
        ]).then(([camRes, statRes]) => {
            setCampaigns(camRes.data);
            setStats(statRes.data);
        }).catch(console.error)
          .finally(() => setLoading(false));
    };

    useEffect(() => { fetchAll(); }, []);

    const handleSend = async (id, e) => {
        e.stopPropagation();
        if (!window.confirm(t('campaigns.confirm.send'))) return;
        setSending(id);
        try {
            await axios.post(`${API_BASE_URL}/api/v1/marketing/campaigns/${id}/send`, {}, { withCredentials: true });
            toast.success(t('campaigns.toast.sent'));
            fetchAll();
        } catch (err) {
            toast.error(err?.response?.data?.message || t('campaigns.toast.send_error'));
        } finally {
            setSending(null);
        }
    };

    const handleDelete = async (id, e) => {
        e.stopPropagation();
        if (!window.confirm(t('campaigns.confirm.delete'))) return;
        try {
            await axios.delete(`${API_BASE_URL}/api/v1/marketing/campaigns/${id}`, { withCredentials: true });
            toast.success(t('campaigns.toast.deleted'));
            fetchAll();
        } catch (err) {
            toast.error(err?.response?.data?.message || t('campaigns.toast.delete_error'));
        }
    };

    const openRate = stats && stats.totalEnvoyes > 0
        ? Math.round((stats.totalOuverts / stats.totalEnvoyes) * 100)
        : 0;

    const columns = [
        {
            accessor: 'nom',
            Header: t('campaigns.column.name'),
            sortable: true,
            minWidth: 280,
            Cell: row => (
                <NavLink to={`/marketing/campaigns/${row.id}`} className="camp-row-link">
                    <div className="camp-nom">{row.nom}</div>
                    {row.sujet && <div className="camp-sujet">{row.sujet}</div>}
                </NavLink>
            ),
        },
        {
            accessor: 'canal',
            Header: t('campaigns.column.canal'),
            sortable: true,
            Cell: row => {
                const m = CANAL_META[row.canal] || CANAL_META.email;
                return (
                    <span className={m.cls}>
                        <FontAwesomeIcon icon={m.icon} /> {m.label}
                    </span>
                );
            },
        },
        {
            accessor: 'statut',
            Header: t('Status'),
            sortable: true,
            Cell: row => {
                const m = STATUT_META[row.statut] || STATUT_META.brouillon;
                return (
                    <span className={m.cls}>
                        <FontAwesomeIcon icon={m.icon} /> {m.label}
                    </span>
                );
            },
        },
        {
            accessor: 'destinataires',
            Header: t('campaigns.column.recipients'),
            sortable: true,
            Cell: row => (row.destinataires || 0).toLocaleString(),
        },
        {
            accessor: 'nbEnvoyes',
            Header: t('campaigns.column.sent'),
            sortable: true,
            Cell: row => (row.nbEnvoyes || 0).toLocaleString(),
        },
        {
            accessor: 'nbOuverts',
            Header: t('campaigns.column.opens'),
            sortable: true,
            Cell: row => {
                const sent = row.nbEnvoyes || 0;
                const opened = row.nbOuverts || 0;
                const rate = sent > 0 ? Math.round((opened / sent) * 100) : 0;
                return sent > 0
                    ? <span className="camp-rate">{opened.toLocaleString()} <em>({rate}%)</em></span>
                    : '—';
            },
        },
        {
            accessor: 'dateEnvoiReel',
            Header: t('campaigns.column.date'),
            sortable: true,
            Cell: row => {
                if (row.dateEnvoiReel) return new Date(row.dateEnvoiReel).toLocaleDateString('fr-FR');
                if (row.dateEnvoiPlanifie) return t('campaigns.date_scheduled_prefix', { date: new Date(row.dateEnvoiPlanifie).toLocaleDateString('fr-FR') });
                return '—';
            },
        },
        {
            accessor: 'actions',
            Header: '',
            Cell: row => (
                <div className="camp-actions" onClick={e => e.stopPropagation()}>
                    {row.statut !== 'envoye' && canEdit && (
                        <button
                            className="camp-icon-btn camp-icon-btn--send"
                            title={t('campaigns.action.send')}
                            disabled={sending === row.id}
                            onClick={e => handleSend(row.id, e)}
                        >
                            <FontAwesomeIcon icon={faPaperPlane} />
                        </button>
                    )}
                    {row.statut !== 'envoye' && canDelete && (
                        <button
                            className="camp-icon-btn camp-icon-btn--danger"
                            title={t('Delete')}
                            onClick={e => handleDelete(row.id, e)}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div className="camp-page">

            {/* Stats */}
            {stats && (
                <div className="camp-stats-row">
                    <StatCard icon={faBullhorn}    label={t('campaigns.stat.total')}     value={stats.total}     color="#2563eb" />
                    <StatCard icon={faCheckCircle} label={t('campaigns.stat.sent')}      value={stats.envoyes}    color="#10b981" />
                    <StatCard icon={faFileAlt}     label={t('campaigns.stat.drafts')}    value={stats.brouillons} color="#f59e0b" />
                    <StatCard icon={faChartBar}    label={t('campaigns.stat.open_rate')} value={`${openRate}%`}   color="#8b5cf6"
                        sub={stats.totalEnvoyes > 0 ? t('campaigns.stat.open_rate_sub', { count: stats.totalOuverts.toLocaleString() }) : undefined}
                    />
                </div>
            )}

            {/* Table */}
            <div className="camp-table-container">
                <Table
                    columns={columns}
                    data={campaigns}
                    mode="client"
                    loading={loading}
                    keyword={keyword}
                    setKeyword={setKeyword}
                    searchPlaceholder={t('campaigns.search_placeholder')}
                    rowId="id"
                    pageSize={20}
                    multiSelect={true}
                />
            </div>

            <FabGroup buttons={canCreate ? [
                { id: 'new', icon: faPlus, label: t('campaign_create.title'), onClick: () => setShowCreate(true) },
            ] : []} />

            {showCreate && canCreate && (
                <CampaignCreateModal
                    onClose={() => setShowCreate(false)}
                    onCreated={fetchAll}
                />
            )}
        </div>
    );
}
